import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AuthUser } from '@sunstone/types';

export type UserTenantType = 'partner' | 'child' | 'pending' | 'none';
// partner  — resolved partner (top-level) tenant
// child    — resolved child tenant
// pending  — fetch in-flight; wait before deciding
// none     — operator_admin (no tenant needed) OR fetch failed/invalid

export interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  userTenantType: UserTenantType;
  sessionExpired: boolean;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  userTenantType: 'none',
  sessionExpired: false,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<AuthUser>) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.user = action.payload;
      state.error = null;
      state.sessionExpired = false;
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.isAuthenticated = false;
      state.user = null;
      state.error = action.payload;
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.error = null;
      state.userTenantType = 'none';
      state.sessionExpired = false;
    },
    sessionExpired: (state) => {
      state.sessionExpired = true;
    },
    clearError: (state) => {
      state.error = null;
    },
    updateUser: (state, action: PayloadAction<Partial<AuthUser>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
    setUserTenantType: (state, action: PayloadAction<UserTenantType>) => {
      state.userTenantType = action.payload;
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  sessionExpired,
  clearError,
  updateUser,
  setUserTenantType,
} = authSlice.actions;

export default authSlice.reducer;
